import { useEffect, useMemo } from "react";
import { StreamShapes, routes } from "@sablier/v2-constants";
import { useNextRouterConfirm } from "@sablier/v2-hooks";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { useRouter } from "next/router";
import { useExtensionUtilities } from "~/client/extensions";
import {
  useAccount,
  useCreateGroupAccessor,
  useCreateGroupField,
  useCreateGroupForm,
  useModalTransaction,
  useTokenBalance,
} from "~/client/hooks";
import type { StreamShape } from "@sablier/v2-constants";
import { useFieldStreams } from "./logic";

export function useInit() {
  const router = useRouter();
  const set = useCreateGroupForm((state) => state.api.setFields);
  const update = useCreateGroupForm((state) => state.api.updateFields);
  const extend = useExtensionUtilities();
  const access = useCreateGroupAccessor();

  useEffect(() => {
    const { isReady, query, replace } = router;

    if (!isReady) {
      return;
    }

    const identifier = _.get(query, "shape")?.toString();
    if (Stream.findShape(identifier).isUnknown) {
      void replace(
        routes.client.pages.createGroup.builder({
          shape: StreamShapes.linear.id,
        }),
      );
    }
  }, [router]);

  useEffect(() => {
    const { isReady, query } = router;

    if (!isReady) {
      return;
    }

    const identifier = _.get(query, "shape")?.toString();
    const shape = Stream.findShape(identifier);
    const { extension, overrides } = extend.initialize({ shape: shape.id });

    const state = access();
    const cloned = state.fields.streams.value.map((s) => {
      return { ...s, extension };
    });

    set({
      streams: {
        value: cloned,
      },
    });

    update({
      shape: {
        value: shape.id as StreamShape,
      },
    });

    if (!_.isNil(overrides)) {
      update(overrides);
    }
  }, [access, extend, router, set, update]);
}

export function useWatcher() {
  const access = useCreateGroupAccessor();
  const token = useCreateGroupField("token");
  const [update, reset] = useCreateGroupForm((state) => [
    state.api.updateFields,
    state.api.resetFields,
  ]);
  const { chainId, isHostSafe } = useAccount();
  const { humanized: balance, isLoading } = useTokenBalance({
    token: token.field.value,
  });

  const { data } = useModalTransaction();
  const { isChanged } = useFieldStreams();

  const isConfirmRequired = useMemo(
    () => isChanged && data.status !== "success" && !isHostSafe,
    [data.status, isChanged, isHostSafe],
  );

  useNextRouterConfirm(isConfirmRequired);

  useEffect(() => {
    const state = access();
    update({
      streams: {
        value: state.fields.streams.value?.map((s) => {
          return { ...s, isLoading };
        }),
      },
      max: {
        value: balance ? balance.toString() : undefined,
      },
    });
  }, [access, balance, isLoading, update]);

  useEffect(() => {
    reset(["token"]);
  }, [chainId, reset]);
}
